import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReactFlow, Node, Edge, Controls, Background, useNodesState, useEdgesState, ConnectionMode, MarkerType, getSmoothStepPath, EdgeLabelRenderer, BaseEdge } from 'reactflow';
import 'reactflow/dist/style.css';

interface Beacon {
  id: string;
  type: string;
  name: string;
  subject_id: string;
  chapter_id: string;
  definition?: string;
  description?: string;
  keywords?: string[];
  relationships?: Array<{
    type: string;
    target_id: string;
    target_name: string;
    target_type: string;
    strength?: number;
    description?: string;
  }>;
}

interface RelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  beacon: Beacon | null;
  onFetchRelationships?: (beaconId: string) => Promise<Beacon>;
}

const nodeTypes = {
  // We can add custom node types here if needed
};

// Custom edge component that renders labels on top
function CustomLabeledEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, data, markerEnd }: any) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: '15px',
            fontWeight: '700',
            color: '#0f172a',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            textShadow: '0 2px 4px rgba(255, 255, 255, 1), 0 1px 2px rgba(255, 255, 255, 0.9)',
            background: '#ffffff',
            border: '3.5px solid #1e293b',
            borderRadius: '10px',
            padding: '12px 20px',
            pointerEvents: 'all',
            zIndex: 9999,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            whiteSpace: 'nowrap',
          }}
          className="nodrag nopan"
        >
          {data?.label}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const edgeTypes = {
  'custom-labeled': CustomLabeledEdge,
};

const getNodeColor = (type: string) => {
  switch (type.toLowerCase()) {
    case 'concept':
      return '#3b82f6'; // blue
    case 'topic':
      return '#10b981'; // green
    case 'figure':
      return '#f59e0b'; // amber
    case 'event':
      return '#ef4444'; // red
    case 'example':
      return '#8b5cf6'; // purple
    default:
      return '#6b7280'; // gray
  }
};

const getEdgeColor = (relationshipType: string) => {
  // Handle reverse relationships with different styling
  if (relationshipType.startsWith('reverse_')) {
    const baseType = relationshipType.replace('reverse_', '');
    switch (baseType.toLowerCase()) {
      case 'related_to':
        return '#9ca3af'; // lighter gray for reverse
      case 'influences':
        return '#fca5a5'; // lighter red for reverse
      case 'part_of':
        return '#93c5fd'; // lighter blue for reverse
      case 'example_of':
        return '#6ee7b7'; // lighter green for reverse
      case 'caused_by':
        return '#fde68a'; // lighter amber for reverse
      default:
        return '#9ca3af';
    }
  }
  
  // Forward relationships with bold colors
  switch (relationshipType.toLowerCase()) {
    case 'related_to':
      return '#6b7280';
    case 'influences':
      return '#ef4444';
    case 'part_of':
      return '#3b82f6';
    case 'example_of':
      return '#10b981';
    case 'caused_by':
      return '#f59e0b';
    default:
      return '#6b7280';
  }
};

export function RelationshipModal({ isOpen, onClose, beacon, onFetchRelationships }: RelationshipModalProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Refs for focus management
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setError(null);
    onClose();
  }, [onClose, setNodes, setEdges]);

  const createGraphData = useCallback(async (beacon: Beacon) => {
    if (!beacon) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch full beacon data with relationships if needed
      let fullBeacon = beacon;
      if (onFetchRelationships && (!beacon.relationships || beacon.relationships.length === 0)) {
        fullBeacon = await onFetchRelationships(beacon.id);
      }

      console.log('Creating graph for beacon:', fullBeacon.name);
      console.log('Beacon relationships count:', fullBeacon.relationships?.length || 0);

      // Create the central node (the selected beacon)
      const centralNode: Node = {
        id: fullBeacon.id,
        type: 'default',
        position: { x: 400, y: 300 },
        data: {
          label: (
            <div className="text-center">
              <div className="font-semibold text-sm text-white drop-shadow-sm">{fullBeacon.name}</div>
              <Badge variant="outline" className="mt-1 text-xs bg-white/20 border-white/40 text-white">
                {fullBeacon.type}
              </Badge>
            </div>
          ),
        },
        style: {
          background: getNodeColor(fullBeacon.type),
          color: 'white',
          border: '3px solid #1f2937',
          borderRadius: '8px',
          padding: '12px',
          minWidth: '140px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      };

      const newNodes: Node[] = [centralNode];
      const newEdges: Edge[] = [];

      // Find all relationships where the selected beacon is either source or target
      const allRelationships: Array<{
        type: string;
        source_id: string;
        source_name: string;
        source_type: string;
        target_id: string;
        target_name: string;
        target_type: string;
        strength?: number;
        description?: string;
        isReverse?: boolean;
      }> = [];

      // Process relationships from the beacon data
      if (fullBeacon.relationships && fullBeacon.relationships.length > 0) {
        console.log('Processing relationships:', fullBeacon.relationships.length);
        
        fullBeacon.relationships.forEach(rel => {
          if (rel.type.startsWith('reverse_')) {
            // This is a reverse relationship - the selected beacon is the target
            allRelationships.push({
              type: rel.type.replace('reverse_', ''),
              source_id: rel.target_id,
              source_name: rel.target_name,
              source_type: rel.target_type,
              target_id: fullBeacon.id,
              target_name: fullBeacon.name,
              target_type: fullBeacon.type,
              strength: rel.strength,
              description: rel.description,
              isReverse: true
            });
          } else {
            // Normal relationship - the selected beacon is the source
            allRelationships.push({
              type: rel.type,
              source_id: fullBeacon.id,
              source_name: fullBeacon.name,
              source_type: fullBeacon.type,
              target_id: rel.target_id,
              target_name: rel.target_name,
              target_type: rel.target_type,
              strength: rel.strength,
              description: rel.description,
              isReverse: false
            });
          }
        });
      }

      console.log('Processed relationships:', allRelationships.length, allRelationships);

      // Create nodes and edges for all found relationships
      if (allRelationships.length > 0) {
        const angleStep = (2 * Math.PI) / allRelationships.length;
        const radius = Math.max(350, allRelationships.length * 50); // Increased radius for better spacing

        allRelationships.forEach((relationship, index) => {
          const angle = index * angleStep;
          const x = 400 + radius * Math.cos(angle);
          const y = 300 + radius * Math.sin(angle);

          // Determine if this beacon is source or target
          const isSource = relationship.source_id === fullBeacon.id;
          const relatedEntityId = isSource ? relationship.target_id : relationship.source_id;
          const relatedEntityName = isSource ? relationship.target_name : relationship.source_name;
          const relatedEntityType = isSource ? relationship.target_type : relationship.source_type;

          console.log('Creating node for:', relatedEntityName, relatedEntityType);

          // Create related node (avoid duplicates)
          if (!newNodes.find(node => node.id === relatedEntityId)) {
            const relatedNode: Node = {
              id: relatedEntityId,
              type: 'default',
              position: { x, y },
              data: {
                label: (
                  <div className="text-center">
                    <div className="font-semibold text-sm text-white drop-shadow-sm">{relatedEntityName}</div>
                    <Badge variant="outline" className="mt-1 text-xs bg-white/20 border-white/40 text-white">
                      {relatedEntityType}
                    </Badge>
                  </div>
                ),
              },
              style: {
                background: getNodeColor(relatedEntityType),
                color: 'white',
                border: '2px solid #374151',
                borderRadius: '6px',
                padding: '10px',
                minWidth: '120px',
                boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
              },
            };

            newNodes.push(relatedNode);
          }

          // Create edge with proper direction
          const edgeId = `${relationship.source_id}-${relationship.target_id}`;
          if (!newEdges.find(edge => edge.id === edgeId)) {
            const isReverse = relationship.isReverse;
            const relationshipLabel = relationship.type
              .replace(/_/g, ' ')
              .toLowerCase()
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            const edge: Edge = {
              id: edgeId,
              source: relationship.source_id,
              target: relationship.target_id,
              type: 'custom-labeled',
              data: {
                label: isReverse ? `← ${relationshipLabel}` : `${relationshipLabel} →`,
              },
              style: {
                stroke: getEdgeColor(isReverse ? `reverse_${relationship.type}` : relationship.type),
                strokeWidth: relationship.strength ? Math.max(4, relationship.strength * 4) : 4,
                strokeDasharray: isReverse ? '5,5' : undefined, // Dashed line for reverse relationships
              },
              animated: false,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: getEdgeColor(isReverse ? `reverse_${relationship.type}` : relationship.type),
                width: 28,
                height: 28,
              },
            };

            newEdges.push(edge);
          }
        });
      }

      console.log('Final nodes:', newNodes.length, newNodes);
      console.log('Final edges:', newEdges.length, newEdges);

      setNodes(newNodes);
      setEdges(newEdges);
    } catch (err) {
      console.error('Error creating graph data:', err);
      setError('Failed to load relationship data');
    } finally {
      setLoading(false);
    }
  }, [onFetchRelationships]);

  useEffect(() => {
    if (isOpen && beacon) {
      createGraphData(beacon);
    }
  }, [isOpen, beacon, createGraphData]);

  // Focus management effect
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is fully rendered and all dropdowns are closed
      const focusTimeout = setTimeout(() => {
        // Remove focus from any elements that might still have it
        const activeEl = document.activeElement;
        if (activeEl && activeEl !== document.body && 'blur' in activeEl) {
          (activeEl as HTMLElement).blur();
        }
        
        // Focus the dialog content to ensure proper modal focus
        if (dialogRef.current) {
          dialogRef.current.focus();
        }
      }, 150);

      // Prevent React Flow and other elements from taking focus
      const preventFocusSteal = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        
        // If the target is within React Flow container or has problematic classes, prevent focus
        if (target && (
          reactFlowWrapperRef.current?.contains(target) ||
          target.classList.contains('bg-popover') ||
          target.closest('[data-radix-popper-content-wrapper]') ||
          target.closest('.react-flow')
        )) {
          e.preventDefault();
          e.stopPropagation();
          
          // Focus the dialog instead
          if (dialogRef.current) {
            dialogRef.current.focus();
          }
        }
      };

      // Use capture phase to intercept focus events early
      document.addEventListener('focusin', preventFocusSteal, true);
      
      // Cleanup
      return () => {
        clearTimeout(focusTimeout);
        document.removeEventListener('focusin', preventFocusSteal, true);
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        ref={dialogRef} 
        className="max-w-[75vw] w-[75vw] max-h-[80vh] p-0 focus:outline-none"
        onOpenAutoFocus={(e) => {
          // Prevent default auto focus behavior
          e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          // Let the parent component handle focus restoration
          e.preventDefault();
        }}
      >
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2">
            Relationship Graph
            {beacon && (
              <Badge variant="outline">
                {beacon.name}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Explore the relationships and connections for this knowledge beacon.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 p-6 pt-2">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-sm text-muted-foreground">Loading relationships...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-red-600 font-medium">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => beacon && createGraphData(beacon)}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : nodes.length === 0 ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-muted-foreground">No relationships found for this beacon.</p>
              </div>
            </div>
          ) : (
            <div 
              ref={reactFlowWrapperRef}
              className="h-96 border rounded-lg overflow-hidden"
              tabIndex={-1}
              onFocus={(e) => {
                // Prevent React Flow container from taking focus
                e.preventDefault();
                if (dialogRef.current) {
                  dialogRef.current.focus();
                }
              }}
            >
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionMode={ConnectionMode.Loose}
                fitView
                fitViewOptions={{ padding: 0.1 }}
                attributionPosition="bottom-left"
                preventScrolling={false}
                zoomOnScroll={false}
                panOnScroll={true}
                selectNodesOnDrag={false}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                zoomOnPinch={false}
                zoomOnDoubleClick={false}
              >
                <Background />
                <Controls />
              </ReactFlow>
            </div>
          )}
        </div>

        {/* Legend */}
        {nodes.length > 0 && (
          <div className="px-6 pb-6">
            <div className="border-t pt-4 space-y-3">
              <div>
                <h4 className="text-sm font-medium mb-2">Entity Types</h4>
                <div className="flex flex-wrap gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    <span>Concept</span>
                  </div>  
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-amber-500"></div>
                    <span>Figure</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-red-500"></div>
                    <span>Event</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-purple-500"></div>
                    <span>Example</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-2">Relationship Direction</h4>
                <div className="flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-px bg-gray-600"></div>
                    <span>→ Forward</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-px border-b border-dashed border-gray-400"></div>
                    <span>← Reverse (dashed)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
